import { MaterialService } from './../../service/material.service';
import { Component, OnDestroy, OnInit, Input } from '@angular/core';
import { Router } from '@angular/router';
import { AuthenticationService } from '../../service/authentification.service';
import { Login } from '../../model/login.model';
import { Auth } from '../../model/auth.model';
import { Observable, Subject, takeUntil } from 'rxjs';
import { HostListener } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { KiwixiGlobals } from '../../kiwixi/kiwixi.globals';
import { FormGroup, FormControl, FormBuilder, Validators } from '@angular/forms';
import { InitAppService } from '../../service/initapp.service';

export enum KEY_CODE {
  RIGHT_ARROW = 39,
  LEFT_ARROW = 37,
  ENTER_KEY = 13
}
@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss', './login.ux.scss'],
  providers: []
})

export class LoginComponent implements OnInit, OnDestroy {

  public login = new Login();
  loading = false;
  returnUrl: string;
  auth: Auth;
  site = '';
  emailFormControl = new FormControl('', [
    Validators.required,
    Validators.email,
  ]);
  matcher;
  passwordFormControl = new FormControl('', [
    Validators.required,
    Validators.minLength(8),
  ]);
  userForm: FormGroup;
  isLoggedIn: Observable<boolean>;
  translatePath = 'login'
  @Input() canLoginSSO = false;
  /** Étape 1 : saisie de l'email seul. Étape 2 : mot de passe (si pas de SSO). */
  step: 'email' | 'password' = 'email';
  checkingSso = false;
  ssoCheckError = false;

  private _unsubscribeAll: Subject<any> = new Subject<any>();

  constructor(
    private builder: FormBuilder,
    private authenticationService: AuthenticationService,
    private kiwixiGlobals: KiwixiGlobals,
    private route: ActivatedRoute,
    private router: Router,
    private initAppService: InitAppService,
    private materialService: MaterialService,
  ) {
    this.isLoggedIn = authenticationService.isLoggedIn();
    this.userForm = this.builder.group({
      email: this.emailFormControl,
      password: this.passwordFormControl
    });
  }

  @HostListener('window:keyup', ['$event'])
  keyEvent(event: KeyboardEvent) {
    if (event.keyCode !== KEY_CODE.ENTER_KEY) {
      return;
    }
    if (!this.canLoginSSO) {
      if (this.userForm.valid) {
        this.checkAuth();
      }
      return;
    }
    if (this.step === 'email' && this.emailFormControl.valid) {
      this.checkEmailStep();
    } else if (this.step === 'password' && this.userForm.valid) {
      this.checkAuth();
    }
  }

  ngOnInit() {
    this.authenticationService.logout();
    this.kiwixiGlobals.setLoginStatus(false);
    this.site = this.route.snapshot.data['site'];
    const routeData = this.route.snapshot.data;
    if (routeData && routeData['canLoginSSO'] !== undefined) {
      this.canLoginSSO = routeData['canLoginSSO'];
    }
    this.onChanges();
    // Revenir à l'étape email si l'utilisateur modifie l'adresse après l'étape 2
    this.emailFormControl.valueChanges.pipe(takeUntil(this._unsubscribeAll)).subscribe(() => {
      if (this.step === 'password') {
        this.step = 'email';
        this.passwordFormControl.setValue('');
      }
    });
    if (this.authenticationService.getCurrentPersona()) {
      this.checkAuth();
    }
    this.authenticationService.currentUserSubject.subscribe(user => {
      this.auth = user;
    })

  }

  ngOnDestroy() {
    // Unsubscribe from all subscriptions
    this._unsubscribeAll.next(null);
    this._unsubscribeAll.complete();
  }

  onChanges(): void {
    this.userForm.valueChanges.subscribe(val => {
      this.login.username = val.email;
      this.login.username = this.login.username.toLocaleLowerCase();
      this.login.password = val.password;
    });
  }

  /**
   * Soumission du formulaire : flux classique (email + mot de passe) si le
   * projet n'a pas activé le SSO sur cette route, sinon flux en 2 étapes.
   */
  onSubmit(): void {
    if (!this.canLoginSSO) {
      this.checkAuth();
      return;
    }
    if (this.step === 'password') {
      this.checkAuth();
    } else {
      this.checkEmailStep();
    }
  }

  /**
   * Étape 1 : valide l'email puis interroge l'API pour savoir si son domaine
   * est routé vers un fournisseur SSO. Si oui, redirection immédiate ; sinon,
   * passage à l'étape mot de passe.
   */
  checkEmailStep(): void {
    if (this.emailFormControl.invalid) {
      this.emailFormControl.markAsTouched();
      return;
    }

    if (!this.canLoginSSO) {
      this.step = 'password';
      return;
    }

    const email = (this.emailFormControl.value || '').toLowerCase();
    this.checkingSso = true;
    this.ssoCheckError = false;
    this.authenticationService.checkSso(email).pipe(takeUntil(this._unsubscribeAll)).subscribe({
      next: (response) => {
        this.checkingSso = false;
        if (response.has_sso && response.sso_url) {
          window.location.href = response.sso_url;
        } else {
          this.step = 'password';
        }
      },
      error: () => {
        // En cas d'échec de la vérification SSO, on ne bloque pas la connexion classique
        this.checkingSso = false;
        this.ssoCheckError = true;
        this.step = 'password';
      }
    });
  }

  backToEmailStep(): void {
    this.step = 'email';
    this.passwordFormControl.setValue('');
  }

  checkAuth() {
    this.loading = true;
    this.authenticationService.newLogin(this.login).pipe(takeUntil(this._unsubscribeAll)).subscribe({
      next: () => {
        this.authenticationService.checkCredentialObservable().pipe(takeUntil(this._unsubscribeAll)).subscribe({
          next: () => {
            this.loading = false;
            const pers = this.authenticationService.getCurrentPersona();
            if (pers) {
              this.kiwixiGlobals.setLoginStatus(true);
              this.initAppService.initApp(false, null);
            }
          },
          error: () => {
            this.loading = false;
            this.kiwixiGlobals.setLoginStatus(false);
            this.materialService.openSnackBar('Erreur login !!!', 'red');
          }
        });
      },
      error: () => {
        this.loading = false;
        this.materialService.openSnackBar('Erreur login !!!', 'red');
      }
    })
  }

  lostPassword() {
    this.router.navigate(['lost-password']);
  }
}
